<template>
  <div id="programForm" class="form">
    <v-card-title>
      {{ id ? "Edit " : "Add " }} {{ terminologies.program }}
      <v-spacer />

      <v-btn icon @click="goBack">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="mt-5">
      <v-form v-if="loaded" ref="form" v-model="valid" lazy-validation>
        <!-- Existing Form Fields -->

        <v-select
          v-if="program.id"
          :items="locations"
          outlined
          v-model="program.centerId"
          clearable
          label="Location"
          item-text="name"
          item-value="id"
          :rules="[rules.required]"
        />
        <v-select
          v-else
          :items="locations"
          outlined
          v-model="selectedLocationIds"
          multiple
          clearable
          label="Location"
          item-text="name"
          item-value="id"
          :rules="[rules.required]"
        />
        <v-text-field
          v-model="program.name"
          outlined
          label="Name"
          :rules="[rules.required]"
        />
        <v-textarea
          outlined
          v-model="program.description"
          label="Description"
          :rows="3"
        />
        <v-text-field
          v-model="program.standardRate"
          outlined
          label="Standard Rate"
          type="number"
          prefix="$"
          :rules="[rules.requiredNumeric, rules.nonNegative, rules.validNumber]"
        />

        <v-select
          v-model="program.payTerm"
          :items="program.relations.payTerm"
          outlined
          label="Payment Terms"
          chips
          deletable-chips
          item-text="label"
          item-value="value"
          :rules="[rules.required]"
        ></v-select>

        <v-text-field
          v-model="program.maxCapacity"
          outlined
          label="Max. Capacity"
          type="number"
        />

        <!-- Updated Switch for Allow Program Overlaps with Info Icon -->
        <div class="d-flex align-center mt-4">
          <v-switch
            v-model="program.allowProgramOverlap"
            label="Allow Program Overlaps"
          ></v-switch>
          <v-btn
            icon
            @click="showOverlapInfo = true"
            class="ml-2"
            :aria-label="'More information about Allow Program Overlaps'"
          >
            <v-icon color="primary">mdi-information</v-icon>
          </v-btn>
        </div>

        <!-- Dialog for Allow Program Overlaps Description -->
        <v-dialog v-model="showOverlapInfo" max-width="500">
          <v-card>
            <v-card-title class="headline">Allow Program Overlaps</v-card-title>
            <v-card-text>
              <p>
                When enabled,
                <strong
                  >other schedules can overlap with schedules under this
                  program</strong
                >. This allows for flexible scheduling where overlapping
                sessions are acceptable.
              </p>
              <p>
                <strong>Enabled:</strong> Other programs can schedule sessions
                that overlap with sessions under this program.
              </p>
              <p>
                <strong>Disabled:</strong> Other programs cannot schedule
                sessions that overlap with sessions under this program, ensuring
                exclusive time slots.
              </p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="showOverlapInfo = false"
                >Close</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-form>

      <v-sheet class="d-md-flex">
        <v-sheet class="flex-column"> </v-sheet>
        <v-switch
          v-if="id"
          v-model="program.inactive"
          flat
          label="Inactive"
        ></v-switch>
        <v-spacer></v-spacer>

        <v-sheet class="justify-right text-right">
          <v-btn @click="goBack" plain class="mr-4"> Back </v-btn>

          <v-btn @click="handleSubmit" color="primary" rounded x-large>
            {{ id ? "Update" : "Save" }}
          </v-btn>
        </v-sheet>
      </v-sheet>
    </v-card-text>
  </div>
</template>


<script>
import { getProgram } from "@/services/program";
import { mapActions, mapGetters } from "vuex";
import { rules } from "../../utils/validation";

export default {
  props: ["id", "isModal"],
  data() {
    return {
      loaded: false,
      valid: false,
      selectedLocationIds: [],
      showOverlapInfo: false,
      program: {
        relations: {
          payTerm: [
            { label: "By Session/Class", value: "session" },
            { label: "Flat Rate", value: "flat rate" },
            { label: "Subscription (Monthly)", value: "subscription" },
          ],
        },
        name: "",
        centerId: null,
        description: "",
        standardRate: "",
        payTerm: "",
        maxCapacity: null,
        inactive: false,
        allowProgramOverlap: false,
      },
      rules: { ...rules },
    };
  },
  created() {
    this.setInstance();
  },
  computed: {
    ...mapGetters("terminology", ["terminologies"]),
    ...mapGetters("auth", ["myCenters"]),
    ...mapGetters("navigation", ["globalCenter"]),
    locations() {
      return this.myCenters.filter((c) => c.id);
    },
  },
  methods: {
    ...mapActions("program", ["createProgram", "updateProgram"]),
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),

    async handleSubmit() {
      if (!this.$refs.form.validate()) return;

      if (this.id) {
        let program = { ...this.program }; // Clone the program object
        delete program.relations;

        this.updateProgram({ program })
          .then((response) => {
            console.log(response);
            this.addSuccessMessage("Program was updated successfully");
            this.$refs.form.reset();
            if (this.isModal) {
              this.onCloseDialog();
            } else {
              this.goBack();
            }
          })
          .catch(() => {
            this.addErrorMessage(
              "Error updating Program, please try again later",
            );
          });
      } else {
        let program = { ...this.program }; // Clone the program object
        delete program.relations;

        this.createProgram({ program, locationIds: this.selectedLocationIds })
          .then((response) => {
            console.log(response);
            this.addSuccessMessage("Program was created successfully");
            this.$refs.form.reset();
            if (this.isModal) {
              this.onCloseDialog();
            } else {
              this.goBack();
            }
          })
          .catch(() => {
            this.addErrorMessage(
              "Error creating Program, please try again later",
            );
          });
      }
    },
    goBack() {
      if (this.isModal) {
        this.onCloseDialog();
      } else {
        this.$router.go(-1);
      }
    },
    setInstance() {
      if (this.id) {
        this.program.id = this.id;
        getProgram(this.id).then((response) => {
          let instance = response.data;
          for (let property in instance) {
            if (
              Object.prototype.hasOwnProperty.call(instance, property) &&
              Object.prototype.hasOwnProperty.call(this.program, property)
            ) {
              this.program[property] = instance[property];
            }
          }

          if (this.program.centerId) {
            this.selectedLocationIds = [this.program.centerId];
          }
          this.loaded = true;
        });
      } else {
        this.loaded = true;
      }
      this.setDependencies();
    },
    setDependencies() {},
    onCloseDialog() {
      this.id = null;
      this.$emit("clicked", "closeProgramDialog");
      this.$refs.form.reset();
    },
  },
  components: {},
};
</script>
